/**
 * Landing Page Builder V2 - Constants
 * Activities: signups/guests/capacity, sharing, and email/SMS blasts.
 */

/** Allowlist of signup_restriction_mode values accepted from clients. Mirrors the DB CHECK constraint. */
export const ALLOWED_SIGNUP_RESTRICTION_MODES = new Set([
  "none",
  "single_choice",
  "multi_choice_restricted",
  "multi_choice_sign_again",
] as const);

/** Generic message returned when an email has an existing active registration that the restriction mode blocks. */
export const ALREADY_SIGNED_UP_MESSAGE = "You're already signed up for this activity.";

/** Generic message returned when the restriction-check query itself can't run. Fails closed. */
export const RESTRICTION_CHECK_UNAVAILABLE_MESSAGE = "Unable to verify signup eligibility. Please try again.";

/** Default max guests an attendee may add per signup when +1 guests are enabled. */
export const DEFAULT_MAX_GUESTS_PER_SIGNUP = 1;

/** Hard ceiling on max_guests_per_signup, enforced server-side. Bounds the attendee-controlled
 *  guest_names array size (sanitizeGuestNames slices to this), independent of admin input. */
export const MAX_GUESTS_PER_SIGNUP_CAP = 20;

/** Max characters stored per guest name (trimmed + capped server-side). */
export const MAX_GUEST_NAME_LENGTH = 100;

/** Upper bound on registrations a single batched guest-edit may touch. A viewer can hold at most
 *  one registration per bookable slot, so this only bounds pathological/forged batch payloads. */
export const MANAGE_GUESTS_MAX_BATCH = 100;

/** Custom SQLSTATE raised by the activity-slot capacity trigger when a registered insert would exceed capacity. */
export const CAPACITY_EXCEEDED_SQLSTATE = "NW100";

/** Custom SQLSTATE raised by enforce_activity_signup_limits() when a registered insert would exceed an activity_day_caps row. */
export const DAY_CAP_EXCEEDED_SQLSTATE = "NW101";

/** Custom SQLSTATE raised by enforce_activity_signup_limits() when a registered insert conflicts with an activity_exclusion_groups row. */
export const EXCLUSION_GROUP_CONFLICT_SQLSTATE = "NW102";

/** Friendly message for a day-cap block (NW101) — checkSignupLimits's pre-check message and the NW101 insert-error fallback. */
export const DAY_CAP_EXCEEDED_MESSAGE = "You've reached the limit of activities you can sign up for on this day.";

/** Friendly message for an exclusion-group block (NW102) — checkSignupLimits's pre-check message and the NW102 insert-error fallback. */
export const EXCLUSION_GROUP_CONFLICT_MESSAGE =
  "This activity conflicts with another activity you're already signed up for.";

/** Short label for the client-side day-cap hint on an ActivityCard/TimeSlotPicker (evaluateRuleBlock). */
export const DAY_CAP_BLOCK_LABEL = "Day limit reached";

/** Short label for the client-side exclusion-group hint on an ActivityCard/TimeSlotPicker (evaluateRuleBlock). */
export const EXCLUSION_GROUP_BLOCK_LABEL = "Conflicts with another signup";

/** Page size when seat-summing a single slot's registrations (the query paginates through all rows). */
export const SLOT_COUNTS_QUERY_LIMIT = 500;

/** Defensive cap on pages when seat-summing one slot. Realistic slots fit in a single page; this
 *  only bounds a pathological row count (max SLOT_COUNTS_QUERY_LIMIT * SLOT_COUNTS_MAX_PAGES rows). */
export const SLOT_COUNTS_MAX_PAGES = 40;

/** Runaway guard for the activity/event-wide seat-sum queries (many slots at once). Set high
 *  enough not to truncate realistic events; it only bounds pathological row counts. */
export const EVENT_SIGNUP_COUNTS_LIMIT = 5000;

/** Fill ratio (registered seats / capacity) at which the capacity view flags a slot as nearly full. */
export const CAPACITY_NEAR_FULL_RATIO = 0.8;

/** How often the capacity view re-fetches signup counts while it and the page are visible. */
export const CAPACITY_POLL_INTERVAL_MS = 30_000;

/** Tick for the capacity view's "Updated Xs ago" label. */
export const CAPACITY_UPDATED_TICK_MS = 1_000;

/**
 * Runaway guard for the viewer's own registration read. Sized like
 * {@link EVENT_SIGNUP_COUNTS_LIMIT} because it sweeps the same population: the prefilter is
 * DOMAIN-wide (`*@company.com`), so at a single-domain company offsite it matches essentially
 * every active registration in the event, not just the viewer's handful.
 *
 * This was 1000, and a 1233-registration offsite silently lost the last 233 rows — attendees
 * whose booking sorted late were shown a "Sign Up" button for an excursion they already held.
 * Truncation here is invisible by construction (a short read looks exactly like "no
 * registrations"), so the caller also reports hitting this cap rather than trusting it.
 */
export const VIEWER_REGISTRATIONS_QUERY_LIMIT = 5000;

/** Delay before ActivitySignupModal retries a register/waitlist call that rejected outright
 *  (network drop/timeout) rather than resolving with a handled `{success:false}`. */
export const REGISTER_NETWORK_RETRY_DELAY_MS = 800;

/** Shown when a register/waitlist call rejects on both the initial attempt and the retry —
 *  distinguishes an unreachable-request failure from a server-returned reason. */
export const REGISTRATION_CONNECTION_ISSUE_MESSAGE = "Connection issue — please check your signal and try again.";

// ============================================================
// Activities
// ============================================================

/** Maximum number of activity labels per event */
export const MAX_ACTIVITY_LABELS = 20;

/** Maximum number of time slots per activity */
export const MAX_TIME_SLOTS_PER_ACTIVITY = 50;

/** Maximum number of custom fields per activity */
export const MAX_CUSTOM_FIELDS_PER_ACTIVITY = 10;

/** Hard cap on emails processed in a single invite-list import call (defense against abuse). */
export const MAX_INVITEES_PER_IMPORT = 5000;

/** Max attendee rows scanned when matching imported invitee emails to the event roster. */
export const ATTENDEE_MATCH_SCAN_LIMIT = 20000;

/** Max registrations scanned when cross-referencing invitees against this activity's RSVPs. */
export const ACTIVITY_RSVP_CROSSREF_LIMIT = 20000;

/** Max invitee rows returned when listing a single activity's invite list. */
export const ACTIVITY_INVITEE_LIST_LIMIT = 20000;

/** Message returned when a non-invited person attempts to sign up for an invite-only activity. */
export const NOT_INVITED_MESSAGE = "This activity is invite only. Your email isn't on the invite list.";

/** Message returned when a viewer without enterprise event access attempts to sign up for a test_publish activity. */
export const TEST_PUBLISH_MESSAGE = "This activity isn't open to the public yet.";

/** Query param on the public landing page that auto-opens an activity's signup modal (deep link). */
export const ACTIVITY_DEEPLINK_PARAM = "activity";

/** Capacity ratio (registered / total) at or above which the activity card's capacity bar turns amber. */
export const CAPACITY_NEARLY_FULL_RATIO = 0.8;

/** Capacity ratio (registered / total) at or above which the bar is considered full and turns red. */
export const CAPACITY_FULL_RATIO = 1;

/** Green fill color for the activity card capacity bar while there is plenty of room. */
export const CAPACITY_AVAILABLE_COLOR = "#22c55e";

/** Amber fill color for the activity card capacity bar once it is nearly full. */
export const CAPACITY_NEARLY_FULL_COLOR = "#f59e0b";

/** Red fill color for the activity card capacity bar once it is full. */
export const CAPACITY_FULL_COLOR = "#ef4444";

// ============================================================
// Activity Signup Limits (day caps + exclusion groups)
// ============================================================

/** Max activityIds accepted per setExclusionGroupMembers call — bounds the membership-verification query. */
export const EXCLUSION_GROUP_MEMBERS_MAX = 200;

/** Max day caps returned/creatable per event (one per date — this bounds the list read, not a real-world constraint). */
export const MAX_DAY_CAPS_PER_EVENT = 1000;

/** Max exclusion groups returned/creatable per event. */
export const MAX_EXCLUSION_GROUPS_PER_EVENT = 500;

/** Max characters accepted for an exclusion group's admin-facing name. */
export const MAX_EXCLUSION_GROUP_NAME_LENGTH = 200;

/** Upper bound on a single day cap's max_count — generous ceiling, just enough to reject garbage input. */
export const MAX_DAY_CAP_COUNT = 1000;

// ============================================================
// Activity Sharing
// ============================================================

/** Base path for the public recipient view of a shared activity's signups (`/share/activity/[token]`). */
export const SHARE_ROUTE_BASE = "/share/activity";

/** Hard cap on recipient emails accepted in a single addShareRecipients call. */
export const MAX_SHARE_RECIPIENTS = 200;

/** Max characters of the optional personal note included in the share-invite email (trimmed + capped server-side). */
export const MAX_SHARE_NOTE_LENGTH = 1000;

/** Max signup rows returned to a recipient's read-only view (caps an unbounded fetch). */
export const MAX_SHARED_ROWS = 1000;

/** Throttle window for stamping a recipient's last_seen_at — at most one write per window per recipient. */
export const SHARE_LAST_SEEN_THROTTLE_MS = 5 * 60 * 1000;

/** Per-user throttle on share reads (defense-in-depth against token probing). */
export const SHARE_ACCESS_RATE_LIMIT = { windowMs: 60 * 1000, maxCalls: 30 };

// ============================================================
// Activity Email Guards
// ============================================================

/** Skip the cancellation email if the registration was created within this window. Protects test signups from spam. */
export const ACTIVITY_EMAIL_TEST_SIGNUP_WINDOW_MS = 5 * 60 * 1000;

/** Belt-and-suspenders dedupe: skip a (registration_id, kind) send when one occurred within this window. */
export const ACTIVITY_EMAIL_DEDUPE_WINDOW_MS = 60 * 1000;

/** Hard cap on the number of recipients a custom-email blast can target in a single call. */
export const ACTIVITY_EMAIL_CUSTOM_BLAST_MAX_RECIPIENTS = 200;

/**
 * Bounds the batched `activities` lookup used to resolve calendar-invite state on the attendee
 * schedule page — well above any real event's activity count, matching `listScheduleItemsPublic`'s
 * own activities batch cap.
 */
export const INVITE_ACTIVITIES_LIMIT = 500;

/** Bounds the batched `activity_time_slots` date lookup for calendar-invite state — same cap convention as {@link INVITE_ACTIVITIES_LIMIT}. */
export const INVITE_TIME_SLOTS_LIMIT = 500;

/**
 * Bounds the viewer's `activity_registrations` lookup on the attendee schedule page; every
 * list query in that file must have a `.limit()` — generous enough for any real attendee's
 * registration count, matching the {@link INVITE_ACTIVITIES_LIMIT} convention.
 */
export const ATTENDEE_REGISTRATIONS_LIMIT = 500;

/**
 * Bounds the batched `activity_email_log` lookup used to determine which email kind actually
 * fired for each registration on the attendee schedule page — same cap convention as
 * {@link INVITE_ACTIVITIES_LIMIT}, sized generously since a single registration logs at most a
 * handful of email sends.
 */
export const INVITE_EMAIL_LOG_LIMIT = 1000;

// ============================================================
// Activity SMS (text-message blasts)
// ============================================================

/** Hard cap on the number of recipients an activity SMS blast can target in a single call. */
export const ACTIVITY_SMS_BLAST_MAX_RECIPIENTS = 200;

/** Max ad-hoc test texts a single user may trigger per minute (anti-abuse on the free-number test send). */
export const SMS_TEST_RATE_LIMIT_PER_MINUTE = 5;

/** Appended to every outbound SMS (unless the body already mentions STOP) for opt-out compliance. */
export const SMS_OPT_OUT_SUFFIX = "\n\nReply STOP to unsubscribe";

/** Standard GSM-7 SMS segment length; drives the editor's segment counter. */
export const SMS_SEGMENT_LENGTH = 160;

/** Max characters accepted for a single SMS draft body (server-enforced). */
export const SMS_DRAFT_BODY_MAX_LENGTH = 1500;

/** Max characters accepted for a draft name. */
export const SMS_DRAFT_NAME_MAX_LENGTH = 120;

/**
 * Merge variables offered in the SMS editor. A plain-text subset of the email
 * {@link TEMPLATE_VARIABLES} — HTML-only / link-button variables are omitted since they don't
 * make sense in a text message. Names match the keys produced by `resolveVariables`.
 */
export const SMS_TEMPLATE_VARIABLES: { name: string; description: string; example: string }[] = [
  { name: "attendee_first_name", description: "Attendee's first name", example: "Alex" },
  { name: "attendee_full_name", description: "Attendee's full name", example: "Alex Chen" },
  { name: "activity_title", description: "Activity title", example: "Morning Run" },
  { name: "activity_date", description: "Activity date", example: "Monday, June 8" },
  { name: "activity_time", description: "Activity time", example: "7:30 AM" },
  {
    name: "activity_when",
    description: "Activity date + time (auto-omits time if missing)",
    example: "Monday, June 8 at 7:30 AM",
  },
  { name: "activity_location", description: "Activity location", example: "Pool area" },
  { name: "event_name", description: "Event name", example: "YC Cancun" },
  { name: "landing_page", description: "Landing page name", example: "YC Cancun 2026" },
  {
    name: "landing_page_link",
    description: "Public URL of the landing page",
    example: "https://www.nowadays.ai/yc-cancun",
  },
];

/** Public origin used to build edit-token deep links in outbound emails. */
export const ACTIVITY_EMAIL_PUBLIC_ORIGIN = "https://www.nowadays.ai";
